import SearchText from "@components/common/search-text";
import { PATHS } from "@constants/Paths";
import TabsLayout from "@layouts/tabs";
import { PeekBox, PeekGrid } from "@piiqtechnologies/ui-components";
import { RefetchProvider, SpaceType, useAccessDevices, useDebounce, usePolicyContext, useSpacesSgt, } from "@piiqtechnologies/ui-lib";
import { useAuthContext } from "@stores/AuthContext";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { AccessDeviceImportProvider } from "../access-devices/AccessDeviceImportContext";
import { AccessDevicesTableConfig } from "../access-devices/AccessDevicesTableConfig";
import AccessDevicesTabAction from "./AccessDevicesTabAction";
import messages from "./messages.i18n";
export const CommunityTabs = ({ community }) => {
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(null);
    const { communityId } = useParams();
    const [searchText, setSearchText] = useState(null);
    const debouncedSearch = useDebounce(searchText, 700);
    const intl = useIntl();
    const [getAccessDevices, accessDevicesResp] = useAccessDevices();
    const [getAvailableSpaces, availableSpacesResp] = useSpacesSgt();
    const [getUnavailableSpaces, unavailableSpacesResp] = useSpacesSgt();
    const { proxyUser } = useAuthContext();
    const { accessDevicesParams } = AccessDevicesTableConfig({
        debouncedSearchKey: searchText,
        community: { _id: communityId },
    });
    const availableUnitsParams = {
        include: "accessDevice",
        "community._id": communityId,
        isVisible: true,
        sgtAvailable: true,
        type: SpaceType.Unit,
        projection: "community,unit,sgtEnabled,type,isVisible,availabilityStatus",
    };
    const unavailableUnitsParams = {
        include: "accessDevice",
        "community._id": communityId,
        sgtAvailable: false,
        type: SpaceType.Unit,
        projection: "community,unit,sgtEnabled,type,isVisible,availabilityStatus",
    };
    useEffect(() => {
        getAccessDevices(Object.assign(Object.assign({}, accessDevicesParams), { community: {
                _id: community._id,
            } }));
        getAvailableSpaces(availableUnitsParams);
        getUnavailableSpaces(unavailableUnitsParams);
    }, [debouncedSearch]);
    const handleTabChange = (value) => {
        navigate(`${communityId}/${value}`);
    };
    const TabAction = useMemo(() => {
        let action = null;
        switch (tabValue) {
            case PATHS.SGT_ACCESS_DEVICES:
                action = (React.createElement(AccessDevicesTabAction, { searchText: searchText, setSearchText: setSearchText, community: community, proxyUser: !!proxyUser }));
                break;
            case PATHS.SGT_UNAVAILABLE_UNITS:
                action = (React.createElement(SearchText, { searchText: searchText, onChange: setSearchText, placeholder: intl.formatMessage(messages.unavailableUnitsSearchPlaceholder) }));
                break;
            case PATHS.SGT_AVAILABLE_UNITS:
                action = (React.createElement(SearchText, { searchText: searchText, onChange: setSearchText, placeholder: intl.formatMessage(messages.availableUnitsSearchPlaceholder) }));
                break;
            case PATHS.SGT_SCHEDULES:
            default:
                break;
        }
        return action;
    }, [tabValue, searchText]);
    useEffect(() => {
        const sgtURLSegments = location.pathname.split("/");
        const activeTabURL = sgtURLSegments.pop() || sgtURLSegments.pop();
        const isValidTabValue = SGT_TABS.map(({ value }) => value).includes(activeTabURL);
        setTabValue((isValidTabValue && activeTabURL) || PATHS.SGT_SCHEDULES);
        setSearchText("");
    }, [location.pathname]);
    const SGT_TABS = useMemo(() => {
        var _a, _b, _c, _d;
        return [
            {
                value: PATHS.SGT_SCHEDULES,
                label: messages.sgtSchedulesTitle,
            },
            {
                value: PATHS.SGT_AVAILABLE_UNITS,
                label: messages.availableUnitsTitle,
                disabled: !usePolicyContext("SGT" /* PolicyActions.SGT */),
                labelBadgeContent: {
                    count: ((_a = availableSpacesResp === null || availableSpacesResp === void 0 ? void 0 : availableSpacesResp.totalCount) === null || _a === void 0 ? void 0 : _a.toString()) || "0",
                },
            },
            {
                value: PATHS.SGT_UNAVAILABLE_UNITS,
                label: messages.unavailableUnitsTitle,
                disabled: !usePolicyContext("SGT" /* PolicyActions.SGT */),
                labelBadgeContent: {
                    count: ((_b = unavailableSpacesResp === null || unavailableSpacesResp === void 0 ? void 0 : unavailableSpacesResp.totalCount) === null || _b === void 0 ? void 0 : _b.toString()) || "0",
                },
            },
            {
                value: PATHS.SGT_ACCESS_DEVICES,
                label: messages.sgtAccessDevicesTitle,
                disabled: !usePolicyContext("SGT" /* PolicyActions.SGT */),
                labelBadgeContent: { count: ((_d = (_c = accessDevicesResp === null || accessDevicesResp === void 0 ? void 0 : accessDevicesResp.data) === null || _c === void 0 ? void 0 : _c.length) === null || _d === void 0 ? void 0 : _d.toString()) || "0" },
            },
        ];
    }, [accessDevicesResp === null || accessDevicesResp === void 0 ? void 0 : accessDevicesResp.data, availableSpacesResp === null || availableSpacesResp === void 0 ? void 0 : availableSpacesResp.totalCount, unavailableSpacesResp === null || unavailableSpacesResp === void 0 ? void 0 : unavailableSpacesResp.totalCount]);
    return (React.createElement(React.Fragment, null,
        React.createElement(PeekGrid, { item: true, xs: 12 },
            React.createElement(PeekBox, { position: "relative", width: "auto", px: 2 },
                React.createElement(RefetchProvider, null,
                    React.createElement(AccessDeviceImportProvider, null,
                        React.createElement(TabsLayout, { tabs: SGT_TABS, activeTab: tabValue, onTabChange: handleTabChange, TabAction: TabAction, TabContent: React.createElement(Outlet, { context: { community, searchText: debouncedSearch } }) })))))));
};
export default CommunityTabs;
